import { classifyPaymentFailure } from '../payments/classify-failure'

/**
 * Mapa de códigos de error de pago de Tours → clave i18n.
 *
 * Mismo contrato que `PAYMENT_ERROR_KEYS` para rentals: cada `code`/`reasonCode`
 * que emiten las edge functions de pago de Tours tiene que estar acá, y cada
 * clave acá tiene que existir en `es.json` y `en.json`. Lo vigila
 * `scripts/validate-tours-payment-reason-codes.mjs` para que no driftee.
 *
 * `toursPaymentErrorKey` cae a un mensaje genérico ante un código desconocido:
 * la UI nunca muestra un código crudo ni revienta.
 */
export const TOURS_PAYMENT_ERROR_KEYS: Record<string, string> = {
  TOURS_BAD_REQUEST: 'tours.errors.bad_request',
  TOURS_FORBIDDEN: 'tours.errors.forbidden',
  TOURS_OPERATOR_REQUIRED: 'tours.errors.operator_required',
  TOURS_UNAUTHORIZED: 'tours.errors.unauthorized',
  TOURS_METHOD_NOT_ALLOWED: 'tours.errors.method_not_allowed',
  TOURS_PROVIDER_UNSUPPORTED: 'tours.errors.provider_unsupported',
  TOURS_BOOKING_NOT_FOUND: 'tours.errors.booking_not_found',
  TOURS_BOOKING_NOT_PAYABLE: 'tours.errors.booking_not_payable',
  TOURS_BOOKING_NOT_PAID: 'tours.errors.booking_not_paid',
  TOURS_PAYMENT_ALREADY_SETTLED: 'tours.errors.payment_already_settled',
  TOURS_PAYMENT_NOT_FOUND: 'tours.errors.payment_not_found',
  TOURS_HOLD_EXPIRED: 'tours.errors.hold_expired',
  TOURS_MIN_LEAD_TIME: 'tours.errors.minimum_lead_time',
  TOURS_MIN_PASSENGERS: 'tours.errors.minimum_passengers',
  TOURS_START_POINT_OUTSIDE_COUNTRY: 'tours.errors.start_point_outside_country',
  TOURS_START_POINT_UNVERIFIED: 'tours.errors.start_point_unverified',
  TOURS_CAPACITY_EXCEEDED: 'tours.errors.capacity_exceeded',
  TOURS_UNIT_UNAVAILABLE: 'tours.errors.unit_unavailable',
  TOURS_UNIT_BUSY: 'tours.errors.unit_busy',
  TOURS_CUSTOM_QUOTE_MISMATCH: 'tours.errors.custom_quote_mismatch',
  TOURS_CUSTOM_QUOTE_CONFLICT: 'tours.errors.custom_quote_conflict',
  TOURS_TERMS_REQUIRED: 'tours.errors.terms_required',
  TOURS_DEPARTURE_CANCELLED: 'tours.errors.departure_cancelled',
  TOURS_DEPARTURE_STARTED: 'tours.errors.departure_started',
  TOURS_NOT_FOUND: 'tours.errors.not_found',
  TOURS_AMOUNT_MISMATCH: 'tours.errors.amount_mismatch',
  TOURS_PAYMENT_FAILED: 'tours.errors.payment_failed',
  TOURS_REFUND_NOT_ALLOWED: 'tours.errors.refund_not_allowed',
  TOURS_REFUND_FAILED: 'tours.errors.refund_failed',
  TOURS_REFUND_RECONCILIATION_REQUIRED: 'tours.errors.refund_reconciliation_required',
  TOURS_SPLIT_FAILED: 'tours.errors.split_failed',
  TOURS_SPLIT_NOT_FOUND: 'tours.errors.split_not_found',
  TOURS_SPLIT_ALREADY_PAID: 'tours.errors.split_already_paid',
  TOURS_HOST_REQUIRED: 'tours.errors.host_required',
  TOURS_REVIEW_NOT_ALLOWED: 'tours.errors.review_not_allowed',
  TOURS_REVIEW_EXISTS: 'tours.errors.review_exists',
  TOURS_WEBHOOK_INVALID: 'tours.errors.webhook_invalid',
  TOURS_DB_ERROR: 'tours.errors.db_error',
  TOURS_INTERNAL_ERROR: 'tours.errors.internal_error',
}

export const TOURS_PAYMENT_ERROR_FALLBACK_KEY = 'tours.errors.generic'

export function toursPaymentErrorKey(code: string | null | undefined): string {
  if (!code) return TOURS_PAYMENT_ERROR_FALLBACK_KEY
  return TOURS_PAYMENT_ERROR_KEYS[code] ?? TOURS_PAYMENT_ERROR_FALLBACK_KEY
}

export function toursChargeFailureI18nKey(err: unknown): string {
  const record =
    err && typeof err === 'object' ? (err as { code?: string; paymentCode?: string }) : {}
  const code = typeof record.code === 'string' ? record.code : null
  if (code && TOURS_PAYMENT_ERROR_KEYS[code]) return TOURS_PAYMENT_ERROR_KEYS[code]

  const message = err instanceof Error ? err.message : null
  const classified = classifyPaymentFailure({
    code: typeof record.paymentCode === 'string' ? record.paymentCode : code,
    message,
  })
  if (classified.code !== 'UNKNOWN') return classified.i18nKey
  return toursPaymentErrorKey(code)
}
